import { useState } from 'react';
import { Plus, FileSpreadsheet, Loader2, Lock, Unlock, FileText, Trash2, ListChecks } from 'lucide-react';
import { useToast } from '../ui/Toast';
import { lohnBuchungsbelegApi } from '../../services/lohnBuchungsbelegApi';
import type { RunSummary, RunDetail, ComputedLine, ComputedCell } from '../../types/lohnBuchungsbeleg';
import { VoucherGrid } from './VoucherGrid';
import { OverrideModal } from './OverrideModal';
import { UploadModal } from './UploadModal';
import { UnmatchedPanel } from './UnmatchedPanel';
import { ChecksumPanel } from './ChecksumPanel';
import type { PrefillLine } from './VorlageTab';

interface Props {
    canEdit: boolean;
    runs: RunSummary[];
    selectedId: number | null;
    detail: RunDetail | null;
    loadingRuns: boolean;
    loadingDetail: boolean;
    onSelectRun: (id: number) => void;
    onReloadRuns: (selectId?: number) => Promise<void>;
    onReloadDetail: (id: number) => Promise<void>;
    onClearDetail: () => void;
    onGenerateJournal: () => void;
    onCreateTemplateLine: (prefill: PrefillLine) => void;
}

function currentMonth(): string {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

export function BelegTab({
    canEdit, runs, selectedId, detail, loadingRuns, loadingDetail,
    onSelectRun, onReloadRuns, onReloadDetail, onClearDetail, onGenerateJournal, onCreateTemplateLine,
}: Props) {
    const { toast } = useToast();
    const [showUpload, setShowUpload] = useState(false);
    const [hideEmpty, setHideEmpty] = useState(false);
    const [override, setOverride] = useState<{ line: ComputedLine; cell: ComputedCell } | null>(null);
    const [busy, setBusy] = useState(false);

    const download = async (format: 'xlsx' | 'pdf') => {
        if (!detail) return;
        try {
            await lohnBuchungsbelegApi.downloadExport(detail.run.id, format, detail.run.period);
        } catch (e) {
            toast(e instanceof Error ? e.message : 'Download fehlgeschlagen', 'error');
        }
    };

    const finalize = async () => {
        if (!detail) return;
        setBusy(true);
        try {
            await lohnBuchungsbelegApi.finalizeRun(detail.run.id);
            toast('Beleg finalisiert', 'success');
            await onReloadRuns(detail.run.id);
            await onReloadDetail(detail.run.id);
        } catch (e) {
            toast(e instanceof Error ? e.message : 'Finalisieren fehlgeschlagen', 'error');
        } finally { setBusy(false); }
    };

    const reopen = async () => {
        if (!detail) return;
        setBusy(true);
        try {
            await lohnBuchungsbelegApi.reopenRun(detail.run.id);
            toast('Beleg wieder geöffnet', 'success');
            await onReloadRuns(detail.run.id);
            await onReloadDetail(detail.run.id);
        } catch (e) {
            toast(e instanceof Error ? e.message : 'Öffnen fehlgeschlagen', 'error');
        } finally { setBusy(false); }
    };

    const remove = async () => {
        if (!detail) return;
        if (!confirm(`Beleg ${detail.run.period} wirklich löschen? Rohdaten, Overrides und Kommentare dieses Monats werden entfernt.`)) return;
        setBusy(true);
        try {
            await lohnBuchungsbelegApi.deleteRun(detail.run.id);
            toast('Beleg gelöscht', 'success');
            onClearDetail();
            await onReloadRuns();
        } catch (e) {
            toast(e instanceof Error ? e.message : 'Löschen fehlgeschlagen', 'error');
        } finally { setBusy(false); }
    };

    const editable = canEdit && detail?.run.status === 'Draft';

    return (
        <div className="flex flex-col gap-3">
            {/* Run list — a horizontal bar above the table so the grid gets the full width. */}
            <div className="flex items-center gap-2">
                <span className="shrink-0 text-xs font-semibold uppercase tracking-wide text-slate-400">Perioden</span>
                {canEdit && (
                    <button onClick={() => setShowUpload(true)} className="inline-flex shrink-0 items-center gap-1 rounded bg-amber-600 px-2 py-1 text-xs font-medium text-white hover:bg-amber-700">
                        <Plus className="h-3.5 w-3.5" /> Neu
                    </button>
                )}
                {loadingRuns ? (
                    <Loader2 className="h-4 w-4 animate-spin text-slate-300" />
                ) : runs.length === 0 ? (
                    <span className="text-xs text-slate-400">Noch keine Belege. {canEdit && 'Lade eine Rohdaten-Datei hoch.'}</span>
                ) : (
                    <div className="flex min-w-0 flex-1 gap-1 overflow-x-auto pb-0.5">
                        {runs.map(r => (
                            <button
                                key={r.id}
                                onClick={() => onSelectRun(r.id)}
                                className={`inline-flex shrink-0 items-center gap-1.5 rounded px-2.5 py-1 text-sm ${selectedId === r.id ? 'bg-amber-50 text-amber-800 ring-1 ring-amber-200' : 'text-slate-600 hover:bg-slate-50'}`}
                            >
                                <span className="font-medium">{r.period}</span>
                                {r.status === 'Final'
                                    ? <Lock className="h-3.5 w-3.5 text-slate-400" />
                                    : <span className="text-[10px] uppercase text-amber-500">Entwurf</span>}
                            </button>
                        ))}
                    </div>
                )}
            </div>

            {/* Detail */}
            <div className="min-w-0">
                {loadingDetail ? (
                    <div className="py-16 text-center text-slate-300"><Loader2 className="mx-auto h-6 w-6 animate-spin" /></div>
                ) : !detail ? (
                    <div className="py-16 text-center text-sm text-slate-400">Keine Periode ausgewählt.</div>
                ) : (
                    <>
                        <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                            <div className="flex items-center gap-3">
                                <h2 className="text-lg font-semibold text-slate-800">Zuordnung {detail.run.period}</h2>
                                {detail.run.status === 'Final'
                                    ? <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-500"><Lock className="h-3 w-3" /> Finalisiert</span>
                                    : <span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs text-amber-700">Entwurf</span>}
                            </div>
                            <div className="flex items-center gap-2">
                                <button onClick={onGenerateJournal} className="inline-flex items-center gap-1 rounded bg-amber-600 px-2.5 py-1.5 text-xs font-medium text-white hover:bg-amber-700">
                                    <ListChecks className="h-4 w-4" /> Buchungsbeleg generieren
                                </button>
                                <button onClick={() => download('xlsx')} className="inline-flex items-center gap-1 rounded border border-slate-300 px-2.5 py-1.5 text-xs text-slate-600 hover:bg-slate-50">
                                    <FileSpreadsheet className="h-4 w-4 text-emerald-600" /> Excel
                                </button>
                                <button onClick={() => download('pdf')} className="inline-flex items-center gap-1 rounded border border-slate-300 px-2.5 py-1.5 text-xs text-slate-600 hover:bg-slate-50">
                                    <FileText className="h-4 w-4 text-rose-600" /> PDF
                                </button>
                                {canEdit && detail.run.status === 'Draft' && (
                                    <button onClick={finalize} disabled={busy} className="inline-flex items-center gap-1 rounded bg-slate-700 px-2.5 py-1.5 text-xs font-medium text-white hover:bg-slate-800 disabled:opacity-50">
                                        {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Lock className="h-3.5 w-3.5" />} Finalisieren
                                    </button>
                                )}
                                {canEdit && detail.run.status === 'Final' && (
                                    <button onClick={reopen} disabled={busy} className="inline-flex items-center gap-1 rounded border border-slate-300 px-2.5 py-1.5 text-xs text-slate-600 hover:bg-slate-50 disabled:opacity-50">
                                        {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Unlock className="h-3.5 w-3.5" />} Öffnen
                                    </button>
                                )}
                                {canEdit && (
                                    <button onClick={remove} disabled={busy} title="Beleg löschen" className="inline-flex items-center gap-1 rounded border border-rose-200 px-2.5 py-1.5 text-xs text-rose-600 hover:bg-rose-50 disabled:opacity-50">
                                        <Trash2 className="h-3.5 w-3.5" /> Löschen
                                    </button>
                                )}
                            </div>
                        </div>

                        {/* Balance indicator + difference analysis */}
                        <ChecksumPanel voucher={detail.voucher} editable={!!editable} />

                        {/* Diagnostics shown ABOVE the grid so they aren't missed. */}
                        <UnmatchedPanel
                            unmatched={detail.voucher.unmatched ?? []}
                            total={detail.voucher.unmatchedTotal ?? 0}
                            unmatchedCostCenters={detail.voucher.unmatchedCostCenters ?? []}
                            unmatchedCostCenterTotal={detail.voucher.unmatchedCostCenterTotal ?? 0}
                            canEdit={editable}
                            onCreateTemplateLine={onCreateTemplateLine}
                            onCostCenterCreated={() => onReloadDetail(detail.run.id)}
                        />

                        <div className="mt-3 flex items-center justify-end">
                            {(() => {
                                const emptyCount = detail.voucher.lines.filter(l => l.cells.every(c => Math.abs(c.final) < 0.005)).length;
                                return (
                                    <label className="inline-flex cursor-pointer items-center gap-2 text-xs text-slate-500">
                                        <span>Leere Zeilen ausblenden{emptyCount > 0 ? ` (${emptyCount})` : ''}</span>
                                        <button
                                            type="button"
                                            role="switch"
                                            aria-checked={hideEmpty}
                                            onClick={() => setHideEmpty(v => !v)}
                                            className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${hideEmpty ? 'bg-amber-500' : 'bg-slate-300'}`}
                                        >
                                            <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${hideEmpty ? 'translate-x-4' : 'translate-x-0.5'}`} />
                                        </button>
                                    </label>
                                );
                            })()}
                        </div>
                        <div className="mt-2">
                            <VoucherGrid
                                voucher={detail.voucher}
                                editable={!!editable}
                                onCellClick={(line, cell) => setOverride({ line, cell })}
                                hideEmpty={hideEmpty}
                            />
                        </div>
                    </>
                )}
            </div>

            {showUpload && (
                <UploadModal
                    defaultPeriod={currentMonth()}
                    onClose={() => setShowUpload(false)}
                    onCreated={(runId) => {
                        setShowUpload(false);
                        toast('Rohdaten importiert', 'success');
                        void onReloadRuns(runId);
                        void onReloadDetail(runId);
                    }}
                />
            )}

            {override && detail && (
                <OverrideModal
                    runId={detail.run.id}
                    line={override.line}
                    cell={override.cell}
                    onClose={() => setOverride(null)}
                    onChanged={() => onReloadDetail(detail.run.id)}
                />
            )}
        </div>
    );
}
